import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, Trash2, Bell } from "lucide-react";

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const { data } = await api.get("/reminders");
    setReminders(data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    await api.patch(`/reminders/${r.id}`, { completed: !r.completed });
    load();
  };

  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/reminders/${id}`); load(); };

  const isOverdue = (d, c) => !c && d && new Date(d) < new Date();

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">FOLLOWUPS</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Reminders</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="create-reminder-btn">
          <Plus className="w-4 h-4" /> NEW REMINDER
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {reminders.length === 0 && (
          <div className="brut-card p-10 text-center text-zinc-500 font-mono text-sm lg:col-span-3">
            NO REMINDERS YET
          </div>
        )}
        {reminders.map((r) => (
          <div key={r.id} className={`brut-card p-4 ${r.completed ? "opacity-50" : ""}`} data-testid={`reminder-${r.id}`}>
            <div className="flex items-start justify-between mb-2">
              <Bell className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className={`font-mono text-[10px] uppercase font-bold px-2 py-0.5 border border-zinc-900 ${isOverdue(r.due_date, r.completed) ? "bg-red-200" : r.completed ? "bg-green-200" : "bg-yellow-200"}`}>
                {isOverdue(r.due_date, r.completed) ? "OVERDUE" : r.completed ? "DONE" : "OPEN"}
              </span>
            </div>
            <div className={`font-bold text-sm ${r.completed ? "line-through" : ""}`}>{r.title}</div>
            {r.description && <p className="text-xs text-zinc-600 mt-1">{r.description}</p>}
            <div className="label-mono mt-2">DUE {r.due_date}</div>
            <div className="flex justify-end gap-1 mt-3">
              <button onClick={() => toggle(r)} className="brut-btn-secondary px-2 py-1 text-xs flex items-center gap-1" data-testid={`toggle-reminder-${r.id}`}>
                <Check className="w-3 h-3" /> {r.completed ? "REOPEN" : "DONE"}
              </button>
              <button onClick={() => del(r.id)} className="text-red-600 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", due_date: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/reminders", form); toast.success("Reminder created"); onCreated(); onClose(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-2xl font-black mb-4">New Reminder</h2>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><div className="label-mono mb-1">TITLE</div><input required className="brut-input w-full px-3 py-2 text-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="reminder-title-input" /></label>
          <label className="block"><div className="label-mono mb-1">DESCRIPTION</div><textarea className="brut-input w-full px-3 py-2 text-sm" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          <label className="block"><div className="label-mono mb-1">DUE DATE</div><input required type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
            <button type="submit" className="brut-btn px-4 py-2 text-sm" data-testid="submit-reminder-btn">CREATE</button>
          </div>
        </form>
      </div>
    </div>
  );
}
